import { createContext, useEffect, useState, useCallback } from 'react';
import { doc, getDoc } from "firebase/firestore";
import { db } from 'firebaseConfig';

const UserContext = createContext();

const userFromStorage = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;

export function UserProvider({ children }) {
    const [user, setUser] = useState(userFromStorage);
    const [managerAccount, setManagerAccount] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        return !!storedUser?.userId;
    });
    const [loading, setLoading] = useState(true);

    // Helper function to fetch Manager Account Details
    const fetchManagerAccount = async (managerId) => {
        if (!managerId) return;
        try {
            const managerDocRef = doc(db, "users", managerId);
            const managerSnap = await getDoc(managerDocRef);
            
            if (managerSnap.exists()) {
                const managerEmail = managerSnap.data().email;
                if (managerEmail) {
                    const accountDocRef = doc(db, "account", managerEmail);
                    const accountSnap = await getDoc(accountDocRef);
                    if (accountSnap.exists()) {
                        setManagerAccount(accountSnap.data());
                    }
                }
            }
        } catch (err) {
            console.error("Error fetching manager account:", err);
        }
    };

    const fetchUserdetails = useCallback(async () => {
        setLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser?.userId) {
                setLoading(false);
                return;
            }

            const collection = currentUser.userType === 'contractor' ? 'contractors' : 'users';
            const docRef = doc(db, collection, currentUser.userId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const userData = docSnap.data();
                const userArray = {
                    email: userData.email,
                    approvedBuildings: userData.approvedBuildings,
                    manager: userData.manager,
                    userId: currentUser.userId,
                    userName: userData.fullName,
                    userType: userData.accountType,
                    buildingId: currentUser.buildingId, // Keep existing building session
                    buildingName: currentUser.buildingName
                };

                setUser(userArray);
                // Fetch manager details immediately after user details are confirmed
                await fetchManagerAccount(userData.manager);
            }
        } catch (err) {
            console.error("Error fetching user details:", err);
        }
        setLoading(false);
    }, []);

    // Re-run whenever authentication state changes
    useEffect(() => {
        if (isAuthenticated) {
            fetchUserdetails();
        } else {
            setManagerAccount(null);
        }
    }, [isAuthenticated, fetchUserdetails]);

    const saveUserDetails = async (userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setIsAuthenticated(true);
        // This ensures data is fetched immediately upon login/building selection
        if (userData.manager) {
            await fetchManagerAccount(userData.manager);
        }
    };

    return (
        <UserContext.Provider value={{ user, managerAccount, saveUserDetails, isAuthenticated, setIsAuthenticated }}>
            {children}
        </UserContext.Provider>
    );
}

export default UserContext;